import React from 'react';
import { ThemeOptions } from '@mui/material/styles';
import { PaletteOptions } from '@mui/material/styles/createPalette';

export const borderRadiusValues = {
  xSmall: 0.25,
  small: 0.5,
  medium: 1,
  large: 3,
};
export const DEFAULT_ELEVATION = 5;

declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      globalContrastText: string;
      chartLabels: string;
      chartLines: string;
      dtpTypes: Record<string, string>;
    };
    customBackgrounds: {
      elevation4: string;
      elevation5: string;
      elevation6: string;
      contrast: string;
      chartTooltip: string;
      chartTooltipValue: string;
    };
  }

  interface PaletteOptions {
    customBackgrounds?: {
      elevation4: string;
      elevation5: string;
      elevation6: string;
      contrast: string;
      chartTooltip: string;
      chartTooltipValue: string;
    };
    custom?: {
      globalContrastText: string;
      chartLabels: string;
      chartLines: string;
      dtpTypes: Record<string, string>;
    };
  }

  interface BreakpointOverrides {
    xs: false; // removes the `xs` breakpoint
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
    laptop: true;
    desktop_small: true;
    desktop_middle: true;
    desktop_large: true;
  }

  // Типография
  interface TypographyVariants {
    f8: React.CSSProperties;
    f8b: React.CSSProperties;
    f10: React.CSSProperties;
    f10b: React.CSSProperties;
    f12: React.CSSProperties;
    f12b: React.CSSProperties;
    f14: React.CSSProperties;
    f14b: React.CSSProperties;
    f16b: React.CSSProperties;
    f16: React.CSSProperties;
    f18: React.CSSProperties;
    f18b: React.CSSProperties;
    f20: React.CSSProperties;
    f22: React.CSSProperties;
    f20b: React.CSSProperties;
    f24: React.CSSProperties;
    f24b: React.CSSProperties;
    f30: React.CSSProperties;
    f32: React.CSSProperties;
    f38b: React.CSSProperties;
    f42: React.CSSProperties;
    f46: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    f8: React.CSSProperties;
    f8b: React.CSSProperties;
    f10: React.CSSProperties;
    f10b: React.CSSProperties;
    f12: React.CSSProperties;
    f12b: React.CSSProperties;
    f14: React.CSSProperties;
    f14b: React.CSSProperties;
    f16b: React.CSSProperties;
    f16: React.CSSProperties;
    f18: React.CSSProperties;
    f18b: React.CSSProperties;
    f20: React.CSSProperties;
    f22: React.CSSProperties;
    f20b: React.CSSProperties;
    f24: React.CSSProperties;
    f24b: React.CSSProperties;
    f30: React.CSSProperties;
    f32: React.CSSProperties;
    f38b: React.CSSProperties;
    f42: React.CSSProperties;
    f46: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    f8: true;
    f8b: true;
    f10: true;
    f10b: true;
    f12: true;
    f12b: true;
    f14: true;
    f14b: true;
    f16b: true;
    f16: true;
    f18: true;
    f18b: true;
    f20: true;
    f22: true;
    f20b: true;
    f24: true;
    f24b: true;
    f30: true;
    f32: true;
    f38b: true;
    f42: true;
    f46: true;
  }
}

export const defaultPalette: PaletteOptions = {} as const;

export const lightThemeParams: ThemeOptions = {
  palette: {
    primary: {
      main: '#9747FF',
      contrastText: 'white',
    },
    secondary: {
      main: '#fff',
      contrastText: '#9747FF',
    },
    text: {
      primary: '#333333',
      secondary: '#8290A1',
    },
    background: {
      default: 'white',
      paper: 'white',
    },
    customBackgrounds: {
      elevation4: '#F8F8F8',
      elevation5: 'white',
      elevation6: '#F2F2F2',
      chartTooltip: '',
      chartTooltipValue: '',
      contrast: 'black',
    },
    ...defaultPalette,
  },
};

export const darkThemeParams: ThemeOptions = {
  palette: {
    primary: {
      main: '#62A744',
      contrastText: 'white',
    },
    mode: 'dark',
    background: {
      // Если забыл, default red для теста
      default: 'red',
      paper: '#2E2E36',
    },
    customBackgrounds: {
      elevation4: '#1A1A22', // Ниже основного - темнее
      elevation5: '#2E2E36', // Считается как основной уровень, должен совпадать с background.paper, так же см components ниже
      elevation6: '#42424B', // Выше - светлее
      contrast: '#F7F7F7', // Цвет контрастный всем основным фонам
      chartTooltip: '#F0F0F01A',
      chartTooltipValue: '#FFFFFF33',
    },
    custom: {
      globalContrastText: '#2E2E37',
      chartLabels: '#74747C',
      chartLines: '#6060681A',
      dtpTypes: {
        '40': '#287CC8', // Столкновение
        '38': '#FF881B', // Наезд на пешехода
        '42': '#35D745', // Наезд на велосепедиста
        '37': '#EFB51F', // Наезд на препятствие
        '41': '#FF3ED5', // Наезд на стоящее ТС
        '39': '#75F4F4', // Падение пассажира
        'other': '#C0C0C0',
      },
    },
    ...defaultPalette,
  },
};

export const commonThemeParams: ThemeOptions = {
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 640,
      laptop: 1024,
      desktop_small: 1280,
      desktop_middle: 1440,
      desktop_large: 1920,
    },
  },
  spacing: 4,
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: 'MoscowSans',
    button: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
    },
    f8: {
      fontSize: '0.5rem',
      lineHeight: 'normal',
    },
    f8b: {
      fontSize: '0.5rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f10: {
      fontSize: '0.625rem',
      lineHeight: 'normal',
    },
    f10b: {
      fontSize: '0.625rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f12: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
    },
    f12b: {
      fontSize: '0.75rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f14: {
      fontSize: '0.875rem',
      lineHeight: 'normal',
    },
    f14b: {
      fontSize: '0.875rem',
      fontWeight: 'bold',
    },
    f16: {
      fontSize: '1rem',
      lineHeight: 'normal',
    },
    f16b: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f18: {
      fontSize: '1.125rem',
      lineHeight: 'normal',
    },
    f18b: {
      fontSize: '1.125rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f20: {
      fontSize: '1.25rem',
      lineHeight: 'normal',
    },
    f22: {
      fontSize: '1.375rem',
      lineHeight: 'normal',
    },
    f20b: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f24: {
      fontSize: '1.5rem',
      lineHeight: 'normal',
    },
    f24b: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f30: {
      fontSize: '1.875rem',
      lineHeight: 'normal',
    },
    f32: {
      fontSize: '2rem',
      lineHeight: 'normal',
    },
    f38b: {
      fontSize: '2.375rem',
      fontWeight: 'bold',
      lineHeight: 'normal',
    },
    f42: {
      fontSize: '2.625rem',
      lineHeight: 'normal',
    },
    f46: {
      fontSize: '2.875rem',
      lineHeight: 'normal',
    },
  },
  components: {
    MuiSkeleton: {
      styleOverrides: {
        root: {
          transform: 'none',
        },
      },
    },
    MuiPaper: {
      // За основу берем значение 5 чтобы был запас для маневров
      defaultProps: {
        elevation: DEFAULT_ELEVATION,
      },
      styleOverrides: {
        // Вырубаем стандартное поведение, чтобы определить точно в соответствии с макетом
        elevation: {
          boxShadow: 'none',
          backgroundImage: 'none',
        },
        elevation6: ({ theme }) => {
          return {
            backgroundColor: theme.palette.customBackgrounds.elevation6,
          };
        },
        elevation5: ({ theme }) => {
          return {
            backgroundColor: theme.palette.customBackgrounds.elevation5,
          };
        },
        elevation4: ({ theme }) => {
          return {
            backgroundColor: theme.palette.customBackgrounds.elevation4,
          };
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'elipsis',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          'textTransform': 'none',
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: theme.spacing(1),
          },
        }),
        sizeLarge: ({ theme }) => ({
          borderRadius:
            borderRadiusValues.large * Number(theme.shape.borderRadius),
          paddingTop: theme.spacing(3),
          paddingBottom: theme.spacing(3),
        }),
        sizeMedium: ({ theme }) => ({
          borderRadius:
            borderRadiusValues.medium * Number(theme.shape.borderRadius),
          paddingTop: theme.spacing(2.5),
          paddingBottom: theme.spacing(2.5),
        }),
        sizeSmall: ({ theme }) => ({
          borderRadius:
            borderRadiusValues.small * Number(theme.shape.borderRadius),
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
  },
};

export const defaultThemeParams: ThemeOptions = {
  ...lightThemeParams,
  ...commonThemeParams,
};
